import React from 'react';
import { X, ChevronLeft, ChevronRight, Home } from 'lucide-react';
import { Alert, AlertFormData } from '../../types/alert.types';
import { useAlertForm } from '../../hooks/useAlertForm';
import { ProgressBar } from './ProgressBar';
import { Step1Name } from './Step1Name';
import { Step2Url } from './Step2Url';
import { Step3Category } from './Step3Category';
import { Step4CategoryFilters } from './Step4CategoryFilters';
import { Step5GeneralOptions } from './Step5GeneralOptions';
import { FilterBadgesSummary } from '../FilterBadgesSummary';

interface AlertModalProps {
  show: boolean;
  editingAlert?: Alert | null;
  onClose: () => void;
  onSave: (formData: AlertFormData) => void;
  embedMode?: boolean;
  isUnlimited?: boolean;
  userPlan?: string;
}

export const AlertModal: React.FC<AlertModalProps> = ({
  show,
  editingAlert,
  onClose,
  onSave,
  embedMode = false,
  isUnlimited = false,
  userPlan = 'free',
}) => {
  const {
    formData,
    currentStep,
    updateField,
    updateMultipleFields,
    resetForm,
    canGoNext,
    nextStep,
    prevStep,
    setCurrentStep,
    goToStep
  } = useAlertForm(editingAlert || undefined);

  if (!show && !embedMode) return null;

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = () => {
    onSave(formData);
    if (!embedMode) handleClose();
  };

  const handleNext = () => {
    if (currentStep === 5) {
      handleSubmit();
    } else if (currentStep === 2 && (formData.vintedUrl || '').trim() !== '') {
      // URL collée : aller directement à l'étape 5
      goToStep(5);
    } else {
      nextStep();
    }
  };

  const content = (
    <div className={`${embedMode ? 'bg-white w-full h-full' : 'bg-white rounded-2xl shadow-2xl max-w-3xl w-full my-8 max-h-[90vh]'} flex flex-col`}>
      {!embedMode && (
        <div className="bg-gradient-to-r from-cyan-500 to-teal-500 px-6 py-4 rounded-t-2xl flex items-center justify-between flex-shrink-0">
          <h2 className="text-2xl font-bold text-white">
            {editingAlert ? 'Modifier l\'alerte' : 'Nouvelle alerte prix'}
          </h2>
          <button
            onClick={handleClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-all z-10 relative"
          >
            <X size={24} />
          </button>
        </div>
      )}

      {/* Progress Bar - pleine largeur, remontée */}
      <div className="flex-shrink-0 w-full">
        <ProgressBar currentStep={currentStep} totalSteps={5} onStepClick={goToStep} />
      </div>

      {/* Badges récapitulatifs des filtres actifs */}
      <div className={`${embedMode ? 'px-0' : 'px-6'} pt-3 flex-shrink-0`}>
        <FilterBadgesSummary
          formData={formData}
          onRemoveCondition={(condition) => updateField('selectedConditions', formData.selectedConditions.filter(c => c !== condition))}
          onRemoveSize={(size) => updateField('selectedSizes', formData.selectedSizes.filter(s => s.id !== size.id))}
          onRemoveColor={(color) => updateField('selectedColors', formData.selectedColors.filter(c => c !== color))}
          onRemoveBrand={(brand) => updateField('selectedBrands', formData.selectedBrands.filter(b => b.id !== brand.id))}
          onRemoveStatus={(statusId) => updateField('selectedStatuses', formData.selectedStatuses.filter(id => id !== statusId))}
          onRemovePlatform={(platform) => updateField('selectedPlatforms', (formData.selectedPlatforms || []).filter(p => p.id !== platform.id))}
          onRemoveRating={(rating) => updateField('selectedRatings', (formData.selectedRatings || []).filter(r => r.id !== rating.id))}
          onRemoveMaterial={(material) => updateField('selectedMaterials', (formData.selectedMaterials || []).filter(m => m.id !== material.id))}
          onRemovePattern={(pattern) => updateField('selectedPatterns', (formData.selectedPatterns || []).filter(p => p.id !== pattern.id))}
          onRemoveLanguage={(language) => updateField('selectedLanguages', (formData.selectedLanguages || []).filter(l => l.id !== language.id))}
          onRemoveInclude={(word) => updateField('mustInclude', formData.mustInclude.filter(w => w !== word))}
          onRemoveExclude={(word) => updateField('mustExclude', formData.mustExclude.filter(w => w !== word))}
          onClearPrice={() => { updateField('minPrice', ''); updateField('maxPrice', ''); }}
          onClearLikes={() => { updateField('minLikes', ''); updateField('maxLikes', ''); }}
          onRemoveCategory={() => updateMultipleFields({ categoryId: '', categoryPath: '' })}
        />
      </div>

      {/* Step Content - scroll unique (vertical + horizontal si besoin) */}
      <form
        id="edit-alert-form"
        onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
        className={`${embedMode ? 'py-6 px-0' : 'p-6'} flex-1 min-h-0 overflow-auto ${currentStep === 5 ? '' : 'min-h-[400px]'}`}
      >
        {currentStep === 1 && (
          <Step1Name
            value={formData.name}
            onChange={(value) => updateField('name', value)}
          />
        )}

        {currentStep === 2 && (
          <Step2Url
            vintedUrl={formData.vintedUrl}
            searchText={formData.searchText || ''}
            onSearchTextChange={(text) => updateMultipleFields({ searchText: text, vintedUrl: '' })}
            onUrlChange={(url) => updateMultipleFields({ vintedUrl: url, searchText: '' })}
          />
        )}

        {currentStep === 3 && (
          <Step3Category
            categoryId={formData.categoryId}
            categoryPath={formData.categoryPath}
            onCategorySelect={(id, path) => {
              updateMultipleFields({ categoryId: id, categoryPath: path });
            }}
            onClearCategory={() => {
              updateMultipleFields({ categoryId: '', categoryPath: '' });
            }}
          />
        )}

        {currentStep === 4 && (
          <Step4CategoryFilters
            categoryId={formData.categoryId}
            categoryPath={formData.categoryPath}
            selectedConditions={formData.selectedConditions}
            selectedSizes={formData.selectedSizes}
            selectedColors={formData.selectedColors}
            selectedBrands={formData.selectedBrands}
            selectedStatuses={formData.selectedStatuses}
            selectedLanguages={formData.selectedLanguages}
            selectedMaterials={formData.selectedMaterials}
            selectedPatterns={formData.selectedPatterns}
            selectedPlatforms={formData.selectedPlatforms}
            selectedRatings={formData.selectedRatings}
            onConditionsChange={(conditions) => updateField('selectedConditions', conditions)}
            onSizesChange={(sizes) => updateField('selectedSizes', sizes)}
            onColorsChange={(colors) => updateField('selectedColors', colors)}
            onBrandsChange={(brands) => updateField('selectedBrands', brands)}
            onStatusesChange={(statuses) => updateField('selectedStatuses', statuses)}
            onLanguagesChange={(languages) => updateField('selectedLanguages', languages)}
            onMaterialsChange={(materials) => updateField('selectedMaterials', materials)}
            onPatternsChange={(patterns) => updateField('selectedPatterns', patterns)}
            onPlatformsChange={(platforms) => updateField('selectedPlatforms', platforms)}
            onRatingsChange={(ratings) => updateField('selectedRatings', ratings)}
          />
        )}

        {currentStep === 5 && (
          <Step5GeneralOptions
            minLikes={formData.minLikes}
            maxLikes={formData.maxLikes}
            minPrice={formData.minPrice}
            maxPrice={formData.maxPrice}
            trendingOnly={formData.trendingOnly}
            maxPages={formData.maxPages}
            mustInclude={formData.mustInclude}
            mustExclude={formData.mustExclude}
            onFieldChange={(field, value) => updateField(field as any, value)}
            isUnlimited={isUnlimited}
            userPlan={userPlan}
          />
        )}
      </form>

      {/* Barre fixe des boutons Précédent / Accueil / Suivant */}
      <div className={`${embedMode ? 'px-0 py-4 border-t border-gray-200' : 'px-6 py-4 bg-gray-50 rounded-b-2xl'} flex items-center justify-between flex-shrink-0 bg-white border-t border-gray-200`}>
        <button
          onClick={prevStep}
          disabled={currentStep === 1}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <ChevronLeft size={20} />
          <span>Précédent</span>
        </button>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-5 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all flex items-center space-x-2"
          >
            <Home size={18} />
            <span>Accueil</span>
          </button>
          {currentStep === 3 && !formData.categoryId && (
            <button
              type="button"
              onClick={() => setCurrentStep(4)}
              className="px-5 py-3 bg-gray-400 text-white rounded-xl hover:bg-gray-500 transition-all"
            >
              Passer cette étape →
            </button>
          )}
        </div>

        <button
          onClick={handleNext}
          disabled={!canGoNext()}
          className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-xl hover:from-cyan-600 hover:to-teal-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 font-bold"
        >
          <span>
            {currentStep === 5
              ? (editingAlert ? 'Modifier l\'alerte' : 'Créer l\'alerte')
              : currentStep === 2 && (formData.vintedUrl || '').trim() !== ''
              ? 'Suivant (étape 5)'
              : 'Suivant'}
          </span>
          {currentStep < 5 && <ChevronRight size={20} />}
        </button>
      </div>
    </div>
  );

  if (embedMode) return content;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      {content}
    </div>
  );
};
